import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Trash2, Send, Loader2, Pause, Play } from 'lucide-react';

const VoiceRecorder = ({ onSend, onCancel, disabled = false, autoStart = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioLevels, setAudioLevels] = useState([]);
  const [showRecorder, setShowRecorder] = useState(autoStart);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];
      
      // Set up audio analysis for waveform
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      microphone.connect(analyser);
      
      analyserRef.current = analyser;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Start waveform animation
      startWaveformAnimation();
      
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check your permissions.');
    }
  };

  const startWaveformAnimation = () => {
    const animate = () => {
      if (analyserRef.current && isRecording && !isPaused) {
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Convert frequency data to waveform bars
        const barCount = 30;
        const newLevels = [];
        const step = Math.floor(bufferLength / barCount);
        
        for (let i = 0; i < barCount; i++) {
          const value = dataArray[i * step] || 0;
          const normalizedValue = value / 255; // Normalize to 0-1
          const height = Math.max(2, normalizedValue * 20 + 2); // Min height 2px, max ~22px
          newLevels.push(height);
        }
        
        setAudioLevels(newLevels);
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };
    animate();
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      // Resume timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Resume waveform animation
      startWaveformAnimation();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const sendRecording = async () => {
    if (!audioBlob) return;
    
    setIsProcessing(true);
    try {
      await onSend(audioBlob);
      deleteRecording();
      setShowRecorder(false);
    } catch (error) {
      console.error('Error sending recording:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCancel = () => {
    deleteRecording();
    setShowRecorder(false);
    onCancel();
  };

  const generateWaveformBars = () => {
    const bars = [];
    const barCount = 20; // Reduced for compact display
    
    for (let i = 0; i < barCount; i++) {
      // Use real audio levels if available, otherwise generate smooth random heights
      let height;
      if (audioLevels[i]) {
        height = audioLevels[i];
      } else {
        // Generate smoother, more realistic waveform
        const baseHeight = 4;
        const variation = Math.sin(i * 0.3) * 8 + Math.random() * 6;
        height = Math.max(baseHeight, baseHeight + variation);
      }
      
      // Add some randomness for more natural look
      const randomVariation = (Math.random() - 0.5) * 2;
      height = Math.max(2, height + randomVariation);
      
      bars.push(
        <div
          key={i}
          className={`w-0.5 rounded-full bg-blue-500 transition-all duration-100 ease-out ${
            isRecording && !isPaused ? 'animate-pulse' : ''
          }`}
          style={{ 
            height: `${Math.min(height, 12)}px`, // Cap height at 12px
            transform: `scaleY(${isPaused ? 0.2 : 1})`,
            opacity: isPaused ? 0.3 : 1,
            animationDelay: `${i * 0.02}s`
          }}
        />
      );
    }
    
    return bars;
  };

  // Auto-start recording when component mounts with autoStart
  useEffect(() => {
    if (autoStart && !isRecording && !audioBlob) {
      startRecording();
    }
  }, [autoStart]);

  if (!showRecorder) {
    return null; // Don't show anything until recording starts
  }

  return (
    <div className="input-field flex-1 bg-secondary-50 border-secondary-200 rounded-lg px-3 py-2 flex items-center justify-between min-h-[2.5rem]">
        {/* Delete Button */}
        <button
          onClick={handleCancel}
          className="p-1 text-gray-500 hover:text-red-500 transition-colors"
          disabled={disabled || isProcessing}
        >
          <Trash2 className="h-4 w-4" />
        </button>

        {/* Recording Status */}
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          <div className="text-gray-700 font-mono text-xs min-w-8">
            {formatTime(recordingTime)}
          </div>
        </div>

        {/* Waveform */}
        <div className="flex items-center space-x-0.5 flex-1 justify-center max-w-32">
          {generateWaveformBars()}
        </div>

        {/* Control Buttons */}
        <div className="flex items-center gap-1">
          {/* Pause/Resume Button */}
          {isRecording && (
            <button
              onClick={isPaused ? resumeRecording : pauseRecording}
              className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
              disabled={disabled || isProcessing}
            >
              {isPaused ? (
                <Play className="h-4 w-4" />
              ) : (
                <Pause className="h-4 w-4" />
              )}
            </button>
          )}

          {/* Send Button */}
          <button
            onClick={isRecording ? stopRecording : sendRecording}
            className={`p-1.5 rounded-full transition-all duration-200 ${
              isRecording 
                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            } ${disabled || isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={disabled || isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isRecording ? (
              <Square className="h-4 w-4" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
    </div>
  );
};

export default VoiceRecorder;
